/** @jsxImportSource preact */
import { h, render } from "preact";

import { readAppearance } from "./appearance";
import type { ChatModule, ChatMountHandle } from "./chat";
import {
  parseWidgetConfig,
  readThemePlacement,
  type WidgetConfig,
} from "./config";
import type {
  Conversation,
  ConversationConfig,
} from "./conversation/conversation";
import {
  conversationStorageKey,
  readConversationSnapshot,
  readWidgetStorage,
  type ConversationSnapshot,
} from "./conversation/snapshot";
import { createModalParking } from "./modal-parking";
import { createElementPicker } from "./picker/picker";
import { createProductContextReader } from "./proactive/page-context";
import {
  createProactiveMessages,
  type ProactiveSession,
} from "./proactive/proactive";
import {
  requestHandshake,
  toHandshakeObservation,
  type HandshakeResult,
} from "./protocol/handshake";
import { isIOS } from "./ui/drawer/storefront-scroll";
import { LauncherRoot } from "./ui/launcher-root";

/** 单次打开或重试等待聊天资源的上限；超时给重试和关闭，不无限等。 */
export const CHAT_LOAD_TIMEOUT_MS = 8_000;

/** 聊天资源接管时入口壳的淡出时长：盖住面板入场的起手延迟，交接不留空档。 */
const ENTRY_HANDOFF_FADE_MS = 160;

/** 一轮聊天资源加载的账本；空闲执行、意图和打开复用同一轮，超时作废让位新一轮。 */
interface InflightLoad {
  /** loadChat 的去重凭据：新一轮（失败或超时后的重试）才递增。 */
  token: number;
  /** 这轮有没有买家在等：打开/重试/静默恢复置真；纯后台轮次为假，着陆只缓存不渲染。 */
  attach: boolean;
  /** 被 8 秒窗口作废：晚到只留模块缓存，不再接管界面。 */
  stale: boolean;
}

export interface MountWidgetDeps {
  /**
   * 拉取并执行完整聊天资源；入口不猜 URL，加载方式由宿主注入。
   * 同一 attempt 的反复调用复用在途下载；
   * 同步返回模块就直接接管——Playground 和打进同一包的调用方不用等、不弹等待卡片。
   */
  loadChat: (attempt: number) => ChatModule | Promise<ChatModule>;
  /**
   * 低优先级预下载聊天资源（只下不执行）。角色画出后调用一次；
   * 执行由 loadChat 在打开意图或宿主页空闲时接管。缺省不预取。
   */
  prefetchChat?: () => void;
  /**
   * 页面在入口脚本下载完成前已经发出的公开握手响应。
   * 正常只消费同一个 Promise；预取传输或读取失败时重新握手一次。
   */
  handshake?: Promise<Response>;
}

/**
 * 挂载阶段只渲染角色入口、主动提示和等待卡片。角色画出后低优先级预下载
 * 完整聊天资源（只下不执行），悬停、触摸或聚焦时立即执行已下载的代码；
 * 宿主页 load 后空闲时也会执行。点入口、点提示或快照里有恢复价值的
 * 状态（开着、有未读、有在途发送）复用同一次在途下载。
 * 主题模态框触发时把整个 host 收进容器，免得焦点被 aria-hidden 圈住。
 */
export function mountWidget(
  container: HTMLElement,
  config: WidgetConfig,
  deps: MountWidgetDeps,
): () => void {
  const parsed = parseWidgetConfig(config);
  const shadowRoot =
    container.shadowRoot ?? container.attachShadow({ mode: "open" });
  const root = document.createElement("div");
  // 入口树和聊天树各由一份 Preact 渲染，容器分开：聊天接管时入口先留着，
  // 等聊天树画出第一帧再撤，交接不断档。
  const entryHost = document.createElement("div");
  const playground = parsed.playgroundSession;
  const readProduct = createProductContextReader(container, parsed);
  if (playground) root.style.height = "100%";
  root.append(entryHost);
  shadowRoot.append(root);
  let disposed = false;
  let appearanceObserver: MutationObserver | undefined;
  let proactive: ReturnType<typeof createProactiveMessages> | undefined;
  // 主题原生 modal 开着时它 flat tree 之外全是 inert：整个挂件寄居进
  // topmost modal 才看得见点得到，胶囊/聊天窗照常展示与交互；modal
  // 关了就搬回来。必须在拾取的 parking 之前建好——同批 modal 变化下各
  // 实例按注册顺序重抬，后注册的压在最上，拾取覆盖层要留在买家 UI 之上。
  const parking = playground
    ? undefined
    : createModalParking({
        hostTag: "gonovus-widget-host",
        home: shadowRoot,
        content: root,
        raise: "parked",
      });
  // iOS 抽屉打开时 body 接手店面滚动，会话必须在 body 外面，惯性才交接得过来（docs/ios-scroll-handoff-research.md）。
  // 所以挂件一挂上就住进 body 外的自有宿主，不搬动主题节点。别改回开合时才搬：搬动那一帧开合过渡全丢，蒙层和入口会一下子出现或消失。
  let outsideHost: HTMLElement | undefined;
  if (parking && isIOS(window)) {
    outsideHost = document.createElement("gonovus-widget-host");
    outsideHost.style.display = "contents";
    const inherited = getComputedStyle(container);
    outsideHost.style.fontFamily = inherited.fontFamily;
    outsideHost.style.direction = inherited.direction;
    const shadow = outsideHost.attachShadow({ mode: "open" });
    document.documentElement.append(outsideHost);
    parking.setHome(shadow);
  }
  // 授权拾取与聊天互不影响：聊天关闭（握手 disabled）时选择器照样能用，
  // Playground 没有店面令牌也不进拾取。data-picker-active 压住主动消息。
  const picker = playground
    ? undefined
    : createElementPicker(container, parsed, shadowRoot);

  /** 聊天资源还没到的阶段：会话没建，意图先攒着。 */
  let snapshot: ConversationSnapshot | undefined;
  let conversation: Conversation | undefined;
  let chatMount: ChatMountHandle | undefined;
  let chatModule: ChatModule | undefined;
  let pendingPrompt: { text: string; question: string } | undefined;
  let phase: "idle" | "loading" | "failed" = "idle";
  let wantsOpen = false;
  // 快照里 open 为真时入口先隐身：收起的角色画出来再被聊天窗换掉是一次
  // 闪烁，买家会看到。聊天资源失败或超时再退回显示入口。
  let dormant = false;
  let loadToken = 0;
  // 进行中的那轮资源加载；空闲执行、意图和打开复用它，作废轮次让位给新一轮。
  let inflight: InflightLoad | undefined;
  // 买家主动打开或重试挂上的 8 秒等待窗口：从本次操作计时，不沿用后台起点。
  let openWait:
    { load: InflightLoad; timer: ReturnType<typeof setTimeout> } | undefined;
  // 角色画出后下个任务才点火的低优先级预下载。
  let prefetchTimer: ReturnType<typeof setTimeout> | undefined;
  // 宿主页 load 后才排程的空闲执行；任一路径点火后都清掉另一半。
  let onWindowLoad: (() => void) | undefined;
  let idleHandle: number | undefined;
  let idleTimer: ReturnType<typeof setTimeout> | undefined;
  // 有一轮失败过：空闲执行不自动补刀，重试只由买家的主动操作发起。
  let loadFailed = false;

  // 后台 Playground 已经拿到令牌和已保存配置，那边没有 App Proxy 可走；
  // 店面先读取开关和配置，不给只浏览页面的买家创建身份。
  const prefetched = deps.handshake;
  let adoptPrefetch = prefetched !== undefined;
  const bootstrap: Promise<HandshakeResult> = playground
    ? Promise.resolve({
        status: "configured",
        settings: playground.settings,
        capabilities: playground.capabilities,
      })
    : requestHandshake(
        parsed.handshakeUrl,
        undefined,
        // 预取的 Response 本体从不被读：每次克隆一份给解析用，
        // 主题编辑器把区块卸载重挂时下一个实例还能拿到完整响应。
        (input, init) => {
          if (adoptPrefetch && prefetched !== undefined) {
            adoptPrefetch = false;
            return prefetched.then((response) => response.clone());
          }
          return fetch(input, init);
        },
        toHandshakeObservation(parsed),
      );
  void bootstrap
    .then((result) => {
      if (disposed) return;
      if (result.status === "failed") {
        console.warn(
          "[GoNovus] Widget handshake failed",
          result.reason,
          result.detail,
        );
        return;
      }
      if (result.status !== "configured") return;
      const { handoff } = result.settings;
      const appearance = readAppearance(result.settings);
      // 每次渲染都重读：主题编辑器的定位覆盖和 Playground 明暗切换会变。
      const readConfig = (): ConversationConfig => ({
        ...parsed,
        get productContext() {
          return readProduct();
        },
        supportEmail: handoff.supportEmail || undefined,
        capabilities: result.capabilities,
        appearance: {
          ...parsed.appearance,
          ...appearance,
          ...(playground &&
          (container.dataset.widgetSurface === "light" ||
            container.dataset.widgetSurface === "dark")
            ? { surface: container.dataset.widgetSurface }
            : {}),
          ...(!playground ? readThemePlacement(container.dataset) : {}),
        },
      });
      if (!playground) {
        const storage = readWidgetStorage();
        snapshot = readConversationSnapshot(
          storage,
          conversationStorageKey(parsed.shopDomain),
        );
        dormant = snapshot?.open === true;
      }
      // 入口阶段会话还没建：主动规则读的 open/draft/未读来自快照，
      // 聊天窗起来以后才换成会话的实时状态。
      const session: ProactiveSession = {
        getState: () =>
          conversation?.getState() ?? {
            open: (snapshot?.open ?? false) || phase !== "idle",
            busy: false,
            disabled: false,
            draft: snapshot?.draft ?? "",
            unreadError: false,
            ...(snapshot?.unreadReplyId === undefined
              ? {}
              : { unreadReplyId: snapshot.unreadReplyId }),
          },
        openWithPrompt: (text, question) => {
          pendingPrompt = { text, question };
          requestChat(true);
        },
      };
      if (!playground) {
        proactive = createProactiveMessages(
          container,
          { ...parsed, appearance },
          readProduct,
          session,
          result.settings.proactiveRules,
          draw,
        );
        proactive.sync();
      }

      function draw() {
        if (disposed) return;
        if (chatMount) {
          chatMount.redraw();
          return;
        }
        if (playground) return;
        render(
          h(LauncherRoot, {
            config: readConfig(),
            prompt: proactive?.getPrompt(),
            dormant,
            card:
              phase === "idle"
                ? undefined
                : {
                    phase,
                    onRetry: () => {
                      requestChat(true);
                    },
                    onClose: () => {
                      phase = "idle";
                      wantsOpen = false;
                      pendingPrompt = undefined;
                      // 等待窗口撤掉，在途下载继续：晚到照常接管成收起态，
                      // 但窗口没了就不会弹回等待或错误卡。
                      disarmWait();
                      draw();
                      proactive?.sync();
                    },
                  },
            onIntent: () => {
              // 悬停、触摸、聚焦都是打开意图：预下载还在路上就接管同一轮，
              // 已下完就立刻执行——买家点开时少等一段。
              ensureChatLoad();
            },
            onOpen: () => {
              requestChat(true);
            },
          }),
          entryHost,
        );
      }

      function requestChat(open: boolean, silent = false) {
        wantsOpen ||= open;
        if (chatMount) {
          const prompt = pendingPrompt;
          pendingPrompt = undefined;
          if (prompt)
            conversation?.openWithPrompt(prompt.text, prompt.question);
          // Playground 常开由 EmbeddedRoot 保证，setOpen 只会往快照里写噪声。
          else if (wantsOpen && !playground) conversation?.setOpen(true);
          wantsOpen = false;
          return;
        }
        // 等待卡片只回应买家自己的点击；换页恢复静默加载。卡片先立起来：
        // 同步到手的模块在同一任务里接管、不画卡片，失败也要能立刻亮错误卡。
        const wantsCard = open && !silent && phase !== "loading";
        if (wantsCard) phase = "loading";
        const round = chatModule === undefined ? ensureChatLoad() : undefined;
        if (chatModule !== undefined) {
          attachChat(chatModule, false);
          return;
        }
        if (round === undefined) {
          // 同步就失败：等着的买家看到错误卡，静默恢复退回入口。
          failWait();
          return;
        }
        // 8 秒窗口挂在当时那轮加载上：预取已在途就从点击起算，不沿用后台起点。
        round.attach = true;
        armWait(round);
        if (wantsCard) {
          draw();
          proactive?.sync();
        }
      }

      /**
       * 点火一轮聊天资源加载。返回还在路上的那轮；同步落地或同步
       * 失败时返回 undefined，调用方按 chatModule 是否到手分辨。
       */
      function ensureChatLoad(): InflightLoad | undefined {
        if (disposed || chatMount !== undefined || chatModule !== undefined)
          return undefined;
        if (inflight !== undefined && !inflight.stale) return inflight;
        const current: InflightLoad = {
          token: ++loadToken,
          attach: false,
          stale: false,
        };
        inflight = current;
        let result: ChatModule | Promise<ChatModule>;
        try {
          result = deps.loadChat(current.token);
        } catch {
          settleLoad(current, undefined);
          return undefined;
        }
        if (result instanceof Promise) {
          void result.then(
            (mod) => {
              settleLoad(current, mod);
            },
            () => {
              settleLoad(current, undefined);
            },
          );
          return current;
        }
        settleLoad(current, result);
        return undefined;
      }

      function settleLoad(
        current: InflightLoad,
        landed: ChatModule | undefined,
      ) {
        if (inflight === current) inflight = undefined;
        // 模块先留下：作废轮次晚到的结果也给重试复用。
        if (landed !== undefined) chatModule = landed;
        if (disposed) return;
        if (openWait?.load === current) {
          clearTimeout(openWait.timer);
          openWait = undefined;
        }
        if (current.stale) return;
        if (landed === undefined) {
          // 失败过的轮次不再被空闲调度自动续上：重试只来自买家操作。
          loadFailed = true;
          // 买家等过的轮次失败才亮错误；后台轮次失败保持静默。
          if (!current.attach) return;
          failWait();
          return;
        }
        // 纯后台轮次着陆只缓存模块：不提前渲染完整聊天窗。
        if (current.attach) attachChat(landed, true);
      }

      /** 把 8 秒等待窗口挂到这一轮加载上；同一轮只挂一次，重复打开不续杯。 */
      function armWait(target: InflightLoad) {
        if (openWait?.load === target) return;
        if (openWait !== undefined) clearTimeout(openWait.timer);
        const timer = setTimeout(() => {
          if (openWait?.timer !== timer) return;
          openWait = undefined;
          // 这一窗口作废：迟到的着陆只留模块缓存，不能再挂载或打开。
          target.stale = true;
          target.attach = false;
          failWait();
        }, CHAT_LOAD_TIMEOUT_MS);
        openWait = { load: target, timer };
      }

      /** 这一轮让买家白等：退回入口并亮错误卡（有卡片在等才切 failed）。 */
      function failWait() {
        dormant = false;
        if (phase === "loading") phase = "failed";
        draw();
        proactive?.sync();
      }

      /** 撤掉等待窗口但不动在途加载：attach 标记留在轮次上，晚到照常接管。 */
      function disarmWait() {
        if (openWait !== undefined) clearTimeout(openWait.timer);
        openWait = undefined;
      }

      function attachChat(mod: ChatModule, deferEntry: boolean) {
        // 幂等：作废轮次晚到又赶上新轮次在途时，模块先被点击接管过一次，
        // 这里再落地不能挂出第二棵聊天树。
        if (disposed || chatMount !== undefined) return;
        // 聊天树接管了，挂着别的轮次的等待窗口就没意义了。
        disarmWait();
        // 焦点在入口树里（键盘点开、卡片按钮）时整棵换掉会掉回 body；
        // 原行为是打开后焦点留在入口按钮上，这里还给聊天树的同一颗。
        // 换页静默恢复时焦点本来就不在壳里，不抢。
        const hostRoot = root.getRootNode();
        const hadFocus =
          hostRoot instanceof ShadowRoot && hostRoot.activeElement !== null;
        const chatHost = document.createElement("div");
        if (playground) chatHost.style.height = "100%";
        root.append(chatHost);
        chatMount = mod.mountChat(chatHost, {
          readConfig,
          getPrompt: () => proactive?.getPrompt(),
          onConversationChange: () => {
            proactive?.sync();
          },
        });
        conversation = chatMount.conversation;
        const prompt = pendingPrompt;
        pendingPrompt = undefined;
        if (prompt) conversation.openWithPrompt(prompt.text, prompt.question);
        else if (wantsOpen && !playground) conversation.setOpen(true);
        wantsOpen = false;
        phase = "idle";
        dormant = false;
        const unmountEntry = () => {
          if (disposed) return;
          render(null, entryHost);
          entryHost.remove();
          if (hadFocus)
            chatHost.querySelector<HTMLElement>(".gn-launcher-toggle")?.focus();
        };
        // 等过的才交接：入口壳淡出到面板入场显现之后——面板第一帧还透明
        // （入场过渡有延迟），整棵硬撤会留一段只有角色的空档。淡出期间
        // 入口不再收事件；同步到手的资源同一任务里换完，不闪卡片。
        const entryShell =
          entryHost.querySelector<HTMLElement>(".gonovus-widget");
        if (deferEntry && entryShell !== null) {
          entryShell.style.transition = `opacity ${String(ENTRY_HANDOFF_FADE_MS)}ms ease`;
          entryShell.style.opacity = "0";
          entryShell.style.pointerEvents = "none";
          setTimeout(unmountEntry, ENTRY_HANDOFF_FADE_MS + 80);
        } else {
          unmountEntry();
        }
      }

      draw();
      appearanceObserver = new MutationObserver(draw);
      appearanceObserver.observe(container, {
        attributes: true,
        attributeFilter: playground
          ? ["data-widget-surface"]
          : [
              "data-widget-position",
              "data-horizontal-spacing",
              "data-bottom-spacing",
            ],
      });
      // Playground 常开、店面有恢复价值的状态（开着、有未读、有在途发送）都直接拉聊天资源。
      // 换页恢复静默拉：手机上恢复成窥视条/半屏抽屉、桌面恢复开着的窗，
      // 都不能先弹一张加载卡盖住页面。
      if (playground) {
        requestChat(true);
      } else {
        if (
          snapshot !== undefined &&
          (snapshot.open ||
            snapshot.unreadReplyId !== undefined ||
            (snapshot.conversationId !== undefined &&
              snapshot.pendingMessageId !== undefined))
        ) {
          requestChat(snapshot.open, true);
        }
        // 角色已经画出：低优先级预下载聊天资源（只下不执行）。
        prefetchTimer = setTimeout(() => {
          prefetchTimer = undefined;
          // 已有轮次或模块在手就不用热缓存：打开意图/恢复已经点火。
          if (
            disposed ||
            chatMount !== undefined ||
            chatModule !== undefined ||
            inflight !== undefined
          )
            return;
          try {
            deps.prefetchChat?.();
          } catch {
            /* 预下载失败不挡路：意图/打开时照常加载。 */
          }
        }, 0);
        // 执行那一下是宿主页主线程上的长任务，别落在店铺加载期间：
        // 等宿主页 load 之后交给浏览器空闲调度；打开意图会抢在它前面执行，
        // 浏览器忽略或延后空闲回调时主动打开也照常走自己的加载。
        onWindowLoad = () => {
          onWindowLoad = undefined;
          if (disposed || loadFailed || inflight !== undefined) return;
          if (typeof window.requestIdleCallback === "function") {
            idleHandle = window.requestIdleCallback(
              () => {
                idleHandle = undefined;
                ensureChatLoad();
              },
              { timeout: 4000 },
            );
          } else {
            idleTimer = setTimeout(() => {
              idleTimer = undefined;
              ensureChatLoad();
            }, 1500);
          }
        };
        if (document.readyState === "complete") onWindowLoad();
        else window.addEventListener("load", onWindowLoad, { once: true });
      }
    })
    .catch((error: unknown) => {
      if (!disposed) {
        console.warn("[GoNovus] Widget mount failed", error);
        render(null, entryHost);
      }
    });

  return () => {
    disposed = true;
    if (prefetchTimer !== undefined) clearTimeout(prefetchTimer);
    if (openWait !== undefined) clearTimeout(openWait.timer);
    if (onWindowLoad !== undefined)
      window.removeEventListener("load", onWindowLoad);
    if (idleHandle !== undefined) window.cancelIdleCallback(idleHandle);
    if (idleTimer !== undefined) clearTimeout(idleTimer);
    appearanceObserver?.disconnect();
    picker?.dispose();
    parking?.dispose();
    proactive?.dispose();
    chatMount?.dispose();
    render(null, entryHost);
    entryHost.remove();
    root.remove();
    outsideHost?.remove();
  };
}
